/** @jsxImportSource @emotion/react */
import Pagination from 'components/atoms/Pagination'
import { Popover } from 'components/atoms/Popover'
import Toast from 'components/atoms/Toast'
import React from 'react'
import { BillingPaymentReceiptDeliveryMethod, BillingPaymentReceiptDto } from 'store/types'
import { getShortChargeIdValue } from 'components/lab/billingCharge/utils'
import {
  useBatchSendPaymentReceiptsMutation,
  useGetPaymentReceiptsQuery,
  useLazyGetPaymentReceiptDownloadQuery,
  useSendPaymentReceiptMutation,
} from 'store/services/paymentReceipts'
import { formatEasternDateTime } from 'helpers/dates'

type Props = {
  patientId: string
}

const formatDateTime = (date?: string | Date | null) => formatEasternDateTime(date, '--')

export const PaymentReceiptsListing: React.FC<Props> = ({ patientId }) => {
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [createdFrom, setCreatedFrom] = React.useState<string>('')
  const [createdTo, setCreatedTo] = React.useState<string>('')
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
  const [activeRowPopover, setActiveRowPopover] = React.useState<string | null>(null)
  const [activeBatchPopover, setActiveBatchPopover] = React.useState<boolean>(false)
  const [sendReceipt] = useSendPaymentReceiptMutation()
  const [batchSend] = useBatchSendPaymentReceiptsMutation()
  const [getDownloadUrl] = useLazyGetPaymentReceiptDownloadQuery()

  const { data, isFetching, refetch } = useGetPaymentReceiptsQuery({
    patientId: Number(patientId),
    page,
    size: pageSize,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
  })

  const toggleSelectAll = () => {
    if (!data?.items?.length) return

    if (selectedIds.size === data.items.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(data.items.map((item) => item.id)))
    }
  }

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)

      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }

      return next
    })
  }

  const handleSend = async (
    receipt: BillingPaymentReceiptDto,
    method: BillingPaymentReceiptDeliveryMethod,
  ) => {
    try {
      await sendReceipt({
        patientId: Number(patientId),
        receiptId: receipt.id,
        deliveryMethods: [method],
      }).unwrap()

      Toast({ type: 'success', label: 'Receipt sent' })
      setActiveRowPopover(null)
      refetch()
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to send receipt'
      Toast({ type: 'error', label: message })
    }
  }

  const handleBatchSend = async (method: BillingPaymentReceiptDeliveryMethod) => {
    try {
      await batchSend({
        patientId: Number(patientId),
        receiptIds: Array.from(selectedIds),
        deliveryMethods: [method],
      }).unwrap()

      Toast({ type: 'success', label: 'Receipts sent' })
      setActiveBatchPopover(false)
      setSelectedIds(new Set())
      refetch()
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to send receipts'
      Toast({ type: 'error', label: message })
    }
  }

  const handleDownload = async (receipt: BillingPaymentReceiptDto) => {
    try {
      const link = await getDownloadUrl({
        receiptId: receipt.id,
        patientId: Number(patientId),
      }).unwrap()

      if (link?.url) {
        window.open(link.url, '_blank', 'noopener,noreferrer')?.focus()
      }

      setActiveRowPopover(null)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to open receipt'
      Toast({ type: 'error', label: message })
    }
  }

  const renderDelivery = (receipt: BillingPaymentReceiptDto) => (
    <div css={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
      {receipt.deliveryMethods?.length
        ? receipt.deliveryMethods.map((method) => (
            <div key={method} css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{method.toUpperCase()}</span>
              <span
                css={{
                  background: '#28a745',
                  color: '#fff',
                  borderRadius: 6,
                  padding: '0.15rem 0.4rem',
                  fontSize: 12,
                }}>
                Sent
              </span>
            </div>
          ))
        : '--'}
    </div>
  )

  return (
    <div>
      <div className="d-flex align-items-end" css={{ gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <label className="textGray mb-1">Created From</label>
          <input
            type="date"
            className="form-control"
            value={createdFrom}
            onChange={(e) => setCreatedFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="textGray mb-1">Created To</label>
          <input
            type="date"
            className="form-control"
            value={createdTo}
            onChange={(e) => setCreatedTo(e.target.value)}
          />
        </div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => {
            setPage(0)
            refetch()
          }}>
          Apply Filters
        </button>
        <div css={{ marginLeft: 'auto', position: 'relative' }}>
          <button
            className="btn btn-primary"
            disabled={!selectedIds.size}
            onClick={() => setActiveBatchPopover(!activeBatchPopover)}>
            Send Selected
          </button>
          <Popover open={activeBatchPopover} setOpen={setActiveBatchPopover}>
            <div css={{ display: 'flex', flexDirection: 'column' }}>
              <button
                className="btn btn-link text-left"
                onClick={() => handleBatchSend(BillingPaymentReceiptDeliveryMethod.SMS)}>
                Send SMS
              </button>
              <button
                className="btn btn-link text-left"
                onClick={() => handleBatchSend(BillingPaymentReceiptDeliveryMethod.EMAIL)}>
                Send Email
              </button>
            </div>
          </Popover>
        </div>
      </div>

      <div className="table-responsive" css={{ width: '100%', overflow: 'visible' }}>
        <table className="table table-sm" style={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={!!data?.items?.length && selectedIds.size === (data?.items?.length || 0)}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Receipt Date</th>
              <th>Scope</th>
              <th>Delivery Method &amp; Status</th>
              <th>Last Sent</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {isFetching && (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {!isFetching && !data?.items?.length && (
              <tr>
                <td colSpan={6} className="text-center">
                  No receipts
                </td>
              </tr>
            )}
            {data?.items?.map((receipt) => (
              <tr key={receipt.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(receipt.id)}
                    onChange={() => toggleRow(receipt.id)}
                  />
                </td>
                <td>{formatDateTime(receipt.createdAt)}</td>
                <td>
                  {receipt.kind === 'SIMPLE' ? (
                    <div>
                      <div>Patient payment {receipt.receiptNumber || ''}</div>
                      {(receipt.cardBrand || receipt.cardLastFour) && (
                        <div css={{ color: '#6c757d', fontSize: 12 }}>
                          {[receipt.cardBrand, receipt.cardLastFour ? `•••• ${receipt.cardLastFour}` : ''].filter(Boolean).join(' ')}
                        </div>
                      )}
                    </div>
                  ) : receipt.paymentId
                    ? 'Payment'
                    : receipt.chargeId
                      ? `Charge ${getShortChargeIdValue(receipt.chargeId)}`
                      : 'Account'}
                </td>
                <td>{renderDelivery(receipt)}</td>
                <td>{formatDateTime(receipt.lastSentAt)}</td>
                <td style={{ position: 'relative' }}>
                  <button
                    className="btn btn-link p-0"
                    onClick={() =>
                      setActiveRowPopover(activeRowPopover === receipt.id ? null : receipt.id)
                    }>
                    <i className="fas fa-ellipsis-h" />
                  </button>
                  <Popover
                    open={activeRowPopover === receipt.id}
                    setOpen={(x) => setActiveRowPopover(x ? receipt.id : null)}>
                    <div css={{ display: 'flex', flexDirection: 'column' }}>
                      <button
                        className="btn btn-link text-left"
                        onClick={() =>
                          handleSend(receipt, BillingPaymentReceiptDeliveryMethod.SMS)
                        }>
                        Send SMS
                      </button>
                      <button
                        className="btn btn-link text-left"
                        onClick={() =>
                          handleSend(receipt, BillingPaymentReceiptDeliveryMethod.EMAIL)
                        }>
                        Send Email
                      </button>
                      <button
                        className="btn btn-link text-left"
                        onClick={() => handleDownload(receipt)}>
                        Download PDF
                      </button>
                      {receipt.stripeReceiptUrl && (
                        <a
                          className="btn btn-link text-left"
                          href={receipt.stripeReceiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setActiveRowPopover(null)}>
                          View Stripe receipt
                        </a>
                      )}
                    </div>
                  </Popover>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        className="pagination-bar"
        currentPage={page}
        totalCount={data?.totalItems || 1}
        pageSize={pageSize}
        siblingCount={1}
        setPageSize={setPageSize}
        onPageChange={setPage}
      />
    </div>
  )
}

export default PaymentReceiptsListing
